import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getWebhookSyncStatus,
  enableWebhookSync,
  disableWebhookSync,
} from '../services/api';
import type { WebhookSyncStatus } from '../types';

/**
 * Phase 3 — F3.3: Webhook Real-Time Sync hooks.
 * TanStack React Query wrappers for the webhook sync API.
 */

/** Fetch webhook sync status. Polls every 30s when enabled. */
export function useWebhookSyncStatus() {
  return useQuery<WebhookSyncStatus>({
    queryKey: ['webhook-sync-status'],
    queryFn: () => getWebhookSyncStatus(),
    staleTime: 30_000,
    refetchInterval: (query) => {
      const enabled = query.state.data?.enabled;
      return enabled ? 30_000 : false;
    },
  });
}

/** Enable webhook sync. Invalidates status on success. */
export function useEnableWebhookSync() {
  const queryClient = useQueryClient();

  return useMutation<{ enabled: boolean; message: string }, Error>({
    mutationFn: () => enableWebhookSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-sync-status'] });
    },
  });
}

/** Disable webhook sync. Invalidates status on success. */
export function useDisableWebhookSync() {
  const queryClient = useQueryClient();

  return useMutation<{ enabled: boolean; message: string }, Error>({
    mutationFn: () => disableWebhookSync(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhook-sync-status'] });
    },
  });
}
